// Marketplace embed bundle entry — autoInit dispatcher.
//
// Scans the DOM for [id^="aldelo-storefront-"] containers, parses each
// container's data-config JSON into a type-safe MarketplaceEmbedConfig,
// and mounts a MarketplaceEmbed per container in an isolated React root
// wrapped in ErrorBoundary. One container crash does NOT affect
// siblings (Doc 05 section 13.2 isolation pattern).
//
// Inline styles only — no Tailwind. NO imports from portal/.

import React from "react";
import { createRoot } from "react-dom/client";
import type { Route } from "./MarketplaceEmbed";
import { MarketplaceEmbed } from "./MarketplaceEmbed";
import { reportLayoutError, reportLayoutReady, startLayoutReporting } from "./iframe-layout";

const MOUNT_PREFIX = "aldelo-storefront-";

// -- ErrorBoundary (inline — keeps bundle entry self-contained) -------------

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  // componentDidMount of a boundary runs after its children committed, i.e. the
  // first real content is in the DOM. Cheap "ready" signal (no data coupling);
  // later height growth as data loads is reported by the ResizeObserver.
  // No-op unless the document is framed (see iframe-layout.ts).
  componentDidMount(): void {
    if (!this.state.hasError) reportLayoutReady();
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    reportLayoutError();
    console.error("[aldelo-embed] Uncaught error in marketplace embed:", error, info);
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <div
          role="alert"
          style={{
            padding: 16,
            borderRadius: 8,
            backgroundColor: "#FEF2F2",
            border: "1px solid #DC2626",
            color: "#DC2626",
            fontSize: 14,
            fontFamily: 'Inter, system-ui, sans-serif',
          }}
        >
          Something went wrong loading the storefront. Please refresh the page.
        </div>
      );
    }
    return this.props.children;
  }
}

// -- Config types + parser ---------------------------------------------------

export interface MarketplaceEmbedConfig {
  view: "catalog" | "signup" | "login" | "account";
  // Optional (Task 2.6): absent ⇒ undefined ⇒ EmbedThemeProvider auto-detects
  // from the host's prefers-color-scheme. An invalid value is treated as
  // absent (graceful) rather than throwing.
  theme?: "light" | "dark";
  apiBase: string;
  recaptchaSiteKey?: string;
  /**
   * Runtime passkey eligibility for THIS mount (RULING R-M, Task 11). Read
   * from data-config at RUNTIME exactly like recaptchaSiteKey above — the
   * shared marketplace.js bundle is ONE build served to every mount, so
   * eligibility must travel per-mount through this attribute, never get
   * inlined into the bundle itself (build-commands.md §57: that is precisely
   * the failure shape of the 2026-08-25 reCAPTCHA incident). Strictly
   * `true`-or-absent: any other value (string "true", 1, etc.) is malformed
   * and defaults to ineligible, same as a missing field — passkeys are only
   * supported on the aldelo.com relying-party origin, and the safe default
   * for every other mount is NO passkey UI, not a best-effort guess.
   */
  passkeyEligible?: boolean;
}

type ViewValue = MarketplaceEmbedConfig["view"];
type ThemeValue = MarketplaceEmbedConfig["theme"];

const VALID_VIEWS: ViewValue[] = ["catalog", "signup", "login", "account"];
const VALID_THEMES: ThemeValue[] = ["light", "dark"];

function isValidView(v: unknown): v is ViewValue {
  return typeof v === "string" && (VALID_VIEWS as string[]).includes(v);
}

function isValidTheme(v: unknown): v is ThemeValue {
  return typeof v === "string" && (VALID_THEMES as string[]).includes(v);
}

export function parseConfig(json: string): MarketplaceEmbedConfig {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    throw new Error("Invalid JSON in data-config");
  }
  if (typeof parsed !== "object" || parsed === null) {
    throw new Error("data-config must be a JSON object");
  }
  const obj = parsed as Record<string, unknown>;

  if (typeof obj.apiBase !== "string" || !obj.apiBase) {
    throw new Error("data-config missing required field: apiBase");
  }
  if (!isValidView(obj.view)) {
    throw new Error("data-config has invalid or missing view");
  }

  const config: MarketplaceEmbedConfig = {
    apiBase: obj.apiBase,
    view: obj.view,
  };

  // Theme is optional (Task 2.6): set it only when explicitly valid.
  // Absent OR invalid ⇒ leave unset (undefined) so the provider auto-detects
  // from the host's prefers-color-scheme. This is a deliberate contract
  // change — theme used to be required and missing/invalid threw.
  if (isValidTheme(obj.theme)) config.theme = obj.theme;

  if (typeof obj.recaptchaSiteKey === "string") config.recaptchaSiteKey = obj.recaptchaSiteKey;

  // Strict `=== true` (not truthy) — a malformed value ("true", 1, "yes")
  // is treated exactly like an absent field: config.passkeyEligible stays
  // undefined, and every downstream consumer's default is ineligible.
  if (obj.passkeyEligible === true) config.passkeyEligible = true;

  return config;
}

// -- view → Route ------------------------------------------------------------

// viewToRoute maps the data-config `view` enum onto the router's Route union.
//
// `login` and `signup` have no Route of their own, because signing in is a GATE
// rather than a page: MarketplaceRouter renders SignInPanel whenever an
// unauthenticated visitor lands on cart / checkout / account / order-detail.
// Routing both to `account` is therefore what actually produces a sign-in UI —
// and, once the visitor is signed in, the account view they were heading for.
// `signup` shares that destination because the OTP flow auto-provisions a
// prospect on first sign-in, so there is no separate registration step.
//
// TD-307a: before this existed, autoInit validated `view` and then dropped it,
// so every container mount fell back to MarketplaceEmbed's `catalog` default.
function viewToRoute(view: ViewValue): Route {
  switch (view) {
    case "catalog":
      return { page: "catalog" };
    case "account":
    case "login":
    case "signup":
      return { page: "account" };
  }
}

// -- Path-based route detection -----------------------------------------------

// Matches /m/cart-review/{uuid} with optional trailing slash.
// The UUID is a lowercase hex string with dashes (standard v4 format).
const CART_REVIEW_PATH_RE = /^\/m\/cart-review\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\/?$/i;

// -- autoInit ----------------------------------------------------------------

// Containers we have already attempted to mount. Guards against double-mount:
// React's createRoot() throws if called twice on the same node, and the
// MutationObserver below re-scans on every DOM mutation (including the subtree
// churn createRoot itself produces). A WeakSet keeps this GC-friendly and
// avoids writing a visible marker attribute into the operator's DOM.
const handled = new WeakSet<Element>();

// mountContainer parses one container's data-config and mounts a
// MarketplaceEmbed into it. Idempotent: a container is processed at most once
// (success OR invalid-config), so repeated scans / observer callbacks never
// double-mount or re-log.
function mountContainer(container: HTMLElement): void {
  if (handled.has(container)) return;
  handled.add(container);

  const configJson = container.getAttribute("data-config");
  if (!configJson) {
    console.error("[aldelo-embed] Missing data-config on", container.id);
    return;
  }

  let config: MarketplaceEmbedConfig;
  try {
    config = parseConfig(configJson);
  } catch (err) {
    console.error("[aldelo-embed] Invalid data-config on", container.id, err);
    return;
  }

  startLayoutReporting();
  const root = createRoot(container);
  root.render(
    <ErrorBoundary>
      <MarketplaceEmbed
        initialView={viewToRoute(config.view)}
        recaptchaSiteKey={config.recaptchaSiteKey}
        theme={config.theme}
        passkeyEligible={config.passkeyEligible}
      />
    </ErrorBoundary>,
  );
}

export function autoInit(): void {
  if (typeof document === "undefined") return;

  // Path-based route detection: check for /m/cart-review/{cart_id} before
  // container scanning. This handles the case where the embed is mounted
  // at a standalone URL (e.g., app.aldelo.ai/m/cart-review/{id}) rather
  // than inside a Framer-hosted page with data-config containers.
  const pathMatch = window.location.pathname.match(CART_REVIEW_PATH_RE);
  if (pathMatch) {
    const cartId = pathMatch[1]!;
    const mountTarget = document.getElementById("root") ?? document.body;
    startLayoutReporting();
    const reactRoot = createRoot(mountTarget);
    reactRoot.render(
      <ErrorBoundary>
        <MarketplaceEmbed
          initialView={{ page: "cart-review", cartId }}
          // Fix-round-2 M-1: explicit, not merely omitted — this standalone
          // mount resolves to the OPERATOR's own domain (production
          // CART_REVIEW_URL_BASE is https://storefront.aldelo.ai), a
          // different registrable domain from the customer relying party
          // (aldelo.com). Passkeys can only ever fail their RP-ID check
          // here, so this must never become eligible — pinned in
          // index.test.tsx so the two mounts cannot diverge again.
          passkeyEligible={false}
        />
      </ErrorBoundary>,
    );
    return;
  }

  // Standard container-scanning: mount on each [id^="aldelo-storefront-"] div.
  document
    .querySelectorAll<HTMLElement>(`[id^="${MOUNT_PREFIX}"]`)
    .forEach((container) => mountContainer(container));
}

// -- observeMounts -----------------------------------------------------------
//
// TD-307b: a one-shot DOMContentLoaded scan loses a race on Framer (and other
// SPA) hosts, where the mount <div> is rendered client-side AFTER the bundle
// runs autoInit — and aldelo.com loads the embed with `async`, so the ordering
// is not guaranteed even on a warm load. Losing that race leaves the host stuck
// on its own "Loading <view>..." placeholder until a manual refresh. A
// MutationObserver watches for mount points that appear after the initial scan,
// covering late client render AND SPA client-side navigation (where
// DOMContentLoaded never re-fires). Mounting is idempotent via `handled`, so the
// initial scan and the observer can both see the same node without
// double-mounting. This mirrors the forms bundle's Bug-A fix.

let mountObserver: MutationObserver | null = null;

export function observeMounts(): void {
  if (typeof document === "undefined" || typeof MutationObserver === "undefined") return;
  if (mountObserver) return; // singleton — one observer per document

  mountObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof HTMLElement)) return;
        // The added node may itself be a mount point, or contain one nested
        // inside wrapper elements (Framer wraps components in layout divs).
        if (node.id && node.id.startsWith(MOUNT_PREFIX)) {
          mountContainer(node);
        }
        node
          .querySelectorAll<HTMLElement>(`[id^="${MOUNT_PREFIX}"]`)
          .forEach((el) => mountContainer(el));
      });
    }
  });

  mountObserver.observe(document.documentElement, { childList: true, subtree: true });
}

export function stopObservingMounts(): void {
  if (mountObserver) {
    mountObserver.disconnect();
    mountObserver = null;
  }
}

// Auto-run: in a browser, scan immediately when possible and arm the observer
// so mounts that appear later (Framer client render, SPA navigation) are
// caught. autoInit + observeMounts share the idempotent `handled` guard.
if (typeof window !== "undefined") {
  const start = (): void => {
    autoInit();
    observeMounts();
  };
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
}
